/**
 * Replacement baselines, value over replacement, scarcity, and roster need
 * (FR-22 – FR-26).
 *
 * This is the part of the tool that reproduces what BeerSheets did: it prices a
 * player not by his raw projected points but by how much better he is than the
 * player you could have instead. A 250-point RB in a league that starts three
 * of them is worth far more than a 250-point QB in a league that starts one.
 *
 * Every baseline is derived from the league's own roster and team counts. None
 * of the three methods contains a hardcoded league size, roster shape, or
 * positional constant that a user's league could contradict (A1, FR-22).
 *
 * Pure module — no I/O.
 */

import type {
  BaselineMethod,
  DraftPick,
  LeagueConfig,
  PlayerId,
  Player,
  Position,
  Projection,
  Valuation,
} from './types.ts';
import { FANTASY_POSITIONS } from './types.ts';
import { startingSlotsByPosition } from './scoring.ts';

/**
 * Man-games demand multipliers for the BEER baseline.
 *
 * The insight (Dupont 2012, popularised by BeerSheets): a league does not
 * consume exactly one player per starting slot over a season. Byes, injuries,
 * and benchings mean each starting slot burns through more than one rosterable
 * player, and it burns through *more* at positions where the starters get hit
 * and *less* at positions that play every week. These multipliers are the
 * "middle ground between VOLS and VORP" the method is named for.
 *
 * Values reflect the usual shape of positional attrition — running backs get
 * hurt, kickers and defenses are streamed weekly, quarterbacks are stable. They
 * multiply league-derived slot counts; they never replace them.
 */
const BEER_DEMAND: Record<Position, number> = {
  QB: 1.35,
  RB: 1.75,
  WR: 1.6,
  TE: 1.4,
  K: 1.1,
  DEF: 1.25,
};

/** How much deeper than the last starter VORP sets its baseline, as a multiple
 *  of league size. One extra round's worth of players at that position. */
const VORP_DEPTH_MULTIPLIER = 1.5;

export interface BaselineResult {
  method: BaselineMethod;
  /** Position → the projected points of the replacement-level player. */
  points: Record<Position, number>;
  /** Position → the 1-based positional rank the baseline was taken at. */
  rank: Record<Position, number>;
}

export interface PoolEntry {
  player: Player;
  position: Position;
  proj_points: number;
  /** 1-based rank within position in the active ranking source. */
  position_rank: number;
  /** 1-based overall rank in the active ranking source, if ranked. */
  overall_rank: number | null;
}

/**
 * Compute the replacement baseline for every position (FR-22).
 *
 * The baseline rank comes from the league; the baseline *points* come from the
 * projection pool, read at that rank. Reading from the pool rather than from a
 * curve means the baseline moves with whatever projection source is loaded.
 */
export function replacementBaselines(
  pool: PoolEntry[],
  league: LeagueConfig,
  method: BaselineMethod,
): BaselineResult {
  const slots = startingSlotsByPosition(league);
  const byPosition = groupByPosition(pool);

  const points = {} as Record<Position, number>;
  const rank = {} as Record<Position, number>;

  for (const pos of FANTASY_POSITIONS) {
    const starters = slots[pos] ?? 0;
    let baselineRank: number;

    switch (method) {
      case 'VOLS':
        // Value over last starter: the last player who would start in week 1
        // somewhere in the league.
        baselineRank = Math.max(1, Math.round(starters));
        break;
      case 'VORP':
        // Value over replacement: deeper, accounting for the fact that the
        // waiver wire is not empty.
        baselineRank = Math.max(1, Math.round(starters * VORP_DEPTH_MULTIPLIER));
        break;
      case 'BEER':
      default:
        // Man-games: how many players at this position the league's rosters
        // will actually consume across a season.
        baselineRank = Math.max(1, Math.round(starters * BEER_DEMAND[pos]));
        break;
    }

    rank[pos] = baselineRank;
    points[pos] = pointsAtRank(byPosition.get(pos) ?? [], baselineRank);
  }

  return { method, points, rank };
}

function groupByPosition(pool: PoolEntry[]): Map<Position, PoolEntry[]> {
  const out = new Map<Position, PoolEntry[]>();
  for (const entry of pool) {
    const arr = out.get(entry.position) ?? [];
    arr.push(entry);
    out.set(entry.position, arr);
  }
  for (const [pos, arr] of out) {
    arr.sort((a, b) => b.proj_points - a.proj_points);
    out.set(pos, arr);
  }
  return out;
}

/**
 * Projected points of the Nth-best player at a position. Past the end of the
 * pool the baseline flattens to the last known value rather than falling to
 * zero — a zero baseline would make every remaining player at that position
 * look like a superstar.
 */
function pointsAtRank(sorted: PoolEntry[], rank: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(Math.max(Math.round(rank) - 1, 0), sorted.length - 1);
  return sorted[idx]!.proj_points;
}

export function valueOverReplacement(
  projPoints: number,
  position: Position,
  baselines: BaselineResult,
): number {
  return projPoints - (baselines.points[position] ?? 0);
}

/**
 * Re-order the board by value rather than by a rank list ("value" mode,
 * FR-16) — the actual signature move of BeerSheets-style cheat sheets: a
 * cross-position ranking by VOR instead of a positional list order.
 *
 * Only the *available* players move. A drafted player's VOR was only ever
 * meaningful while he was still on the board — the replacement baseline has
 * shifted since he left it, so re-pricing him now would be fiction. Drafted
 * players keep whatever order the underlying rank blend gave them, which is
 * exactly enough to render a sensible position in a struck-through row.
 */
export function orderByValue(
  availableIds: PlayerId[],
  draftedIdsInRankOrder: PlayerId[],
  valuations: Map<PlayerId, Valuation>,
): PlayerId[] {
  const byVor = [...availableIds].sort(
    (a, b) => (valuations.get(b)?.vor ?? -Infinity) - (valuations.get(a)?.vor ?? -Infinity),
  );
  return [...byVor, ...draftedIdsInRankOrder];
}

// ---------------------------------------------------------------------------
// Positional scarcity (FR-24)
// ---------------------------------------------------------------------------

export interface ScarcityState {
  /** Position → share of the last N picks spent there. */
  recent_rate: Record<Position, number>;
  /** Position → how many undrafted players remain in the current top tier. */
  tier_remaining: Record<Position, number>;
  /** Position → scarcity score in roughly [0, 1]. */
  score: Record<Position, number>;
  /** Positions currently experiencing a run, for the UI banner. */
  runs: Position[];
  window: number;
}

/**
 * Detect positional runs and tier cliffs.
 *
 * Two independent pressures are combined. **Rate**: if six of the last twelve
 * picks were running backs, running backs are draining twice as fast as a
 * uniform draft would predict. **Tier depth**: if only two players remain in
 * the current tier at a position, the price of waiting is a whole tier, not one
 * player. The second matters more late; the first matters more early.
 */
export function computeScarcity(
  picks: DraftPick[],
  available: PoolEntry[],
  tiers: Map<PlayerId, number | null>,
  window = 12,
  positionOf?: (id: PlayerId) => Position | null,
): ScarcityState {
  const recent = picks.slice(-window);
  const counts = {} as Record<Position, number>;
  const recent_rate = {} as Record<Position, number>;
  const tier_remaining = {} as Record<Position, number>;
  const score = {} as Record<Position, number>;

  for (const pos of FANTASY_POSITIONS) counts[pos] = 0;

  for (const pick of recent) {
    const pos = positionOf?.(pick.player_id) ?? null;
    if (pos) counts[pos]++;
  }

  const byPosition = groupByPosition(available);
  const runs: Position[] = [];

  // A uniform draft would spend picks across positions in proportion to how
  // many of each are still worth taking; using the available pool as the
  // expectation keeps this honest late in the draft when only two positions
  // remain in play.
  const totalAvailable = available.length || 1;

  for (const pos of FANTASY_POSITIONS) {
    const rate = recent.length === 0 ? 0 : counts[pos] / recent.length;
    recent_rate[pos] = rate;

    const posPool = byPosition.get(pos) ?? [];
    const expected = posPool.length / totalAvailable;
    // How much faster than expected this position is draining.
    const pressure = expected <= 0 ? 0 : Math.max(0, rate - expected) / Math.max(expected, 0.05);

    const topTier = firstTier(posPool, tiers);
    const remaining = topTier === null
      ? posPool.length
      : posPool.filter((e) => tiers.get(e.player.player_id_sleeper) === topTier).length;
    tier_remaining[pos] = remaining;

    // A thin tier is urgent; a deep one is not. Saturates at ~6 remaining.
    const cliff = remaining <= 0 ? 0 : Math.max(0, 1 - remaining / 6);

    score[pos] = clamp01(0.6 * clamp01(pressure) + 0.4 * cliff);

    // A run is a position going *faster than the board would predict*, not
    // simply a position with a lot of recent picks. In a two-position pool an
    // even 50/50 split is uniform, not a run; in a six-position pool the same
    // 50% is a stampede. Measuring against the available pool is what makes
    // the signal mean the same thing in round two and round twelve.
    if (pressure >= 0.25 && counts[pos] >= 3) runs.push(pos);
  }

  return { recent_rate, tier_remaining, score, runs, window };
}

function firstTier(pool: PoolEntry[], tiers: Map<PlayerId, number | null>): number | null {
  for (const entry of pool) {
    const tier = tiers.get(entry.player.player_id_sleeper);
    if (tier !== null && tier !== undefined) return tier;
  }
  return null;
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

// ---------------------------------------------------------------------------
// Roster need (FR-25)
// ---------------------------------------------------------------------------

export interface RosterNeed {
  /** Position → marginal starter value in [0, 1]. */
  score: Record<Position, number>;
  /** Position → how many starting slots the user still has open there. */
  open_starters: Record<Position, number>;
  /** How many flex slots remain open, by flex kind. */
  open_flex: { kind: string; eligible: Position[]; count: number }[];
  /** Positions with no starter at all yet — the UI calls these out. */
  unfilled: Position[];
}

/**
 * Marginal value of adding one more player at each position, given the user's
 * current roster.
 *
 * The rule this is built around (FR-25): depth is discounted, not zeroed. A
 * third running back when both RB slots are full is worth less than a first
 * tight end — but it is not worth nothing, because flex slots exist, because
 * starters get hurt, and because the best bench player at a deep position
 * routinely outscores the worst starter at a shallow one. Zeroing depth is the
 * classic way a draft tool talks you into a kicker in round nine.
 */
export function computeRosterNeed(
  roster: PlayerId[],
  league: LeagueConfig,
  positionOf: (id: PlayerId) => Position | null,
): RosterNeed {
  const owned = {} as Record<Position, number>;
  for (const pos of FANTASY_POSITIONS) owned[pos] = 0;
  for (const id of roster) {
    const pos = positionOf(id);
    if (pos) owned[pos]++;
  }

  const open_starters = {} as Record<Position, number>;
  const remaining = { ...owned };

  for (const pos of FANTASY_POSITIONS) {
    const required = league.starters_by_position[pos] ?? 0;
    const filled = Math.min(remaining[pos], required);
    remaining[pos] -= filled;
    open_starters[pos] = required - filled;
  }

  // Leftovers spill into flex slots, cheapest-position-first is not meaningful
  // here, so fill each flex with whatever is spare and eligible.
  const open_flex = league.flex_slots.map((f) => ({ ...f }));
  for (const flex of open_flex) {
    while (flex.count > 0) {
      const donor = flex.eligible.find((p) => remaining[p] > 0);
      if (!donor) break;
      remaining[donor]--;
      flex.count--;
    }
  }

  const score = {} as Record<Position, number>;
  const unfilled: Position[] = [];

  for (const pos of FANTASY_POSITIONS) {
    const required = league.starters_by_position[pos] ?? 0;
    const eligibleFlex = open_flex
      .filter((f) => f.eligible.includes(pos))
      .reduce((acc, f) => acc + f.count, 0);

    if (required === 0 && eligibleFlex === 0) {
      // The league does not start this position at all (no K, no DEF).
      score[pos] = 0;
      continue;
    }

    if (open_starters[pos] > 0) {
      // Unfilled starting slot: full need, scaled slightly by how many are open.
      score[pos] = clamp01(0.8 + 0.2 * Math.min(open_starters[pos] / Math.max(required, 1), 1));
      if (owned[pos] === 0) unfilled.push(pos);
      continue;
    }

    if (eligibleFlex > 0) {
      score[pos] = 0.55;
      continue;
    }

    // Pure depth. Decays with each additional body but never reaches zero.
    const depth = owned[pos] - required;
    score[pos] = clamp01(0.35 / (1 + depth * 0.6));
  }

  return { score, open_starters, open_flex, unfilled };
}

/**
 * How much your specific roster changes what a player is worth, in points,
 * relative to the generic league-wide VOR the board already prices him at.
 *
 * `computeRosterNeed` above answers "how many slots are open at each position",
 * which is what the UI needs and what the old score term used. But it is a
 * *per-position* number, so it cannot discriminate between two players at the
 * same position — and measured against a real board it barely discriminated at
 * all: on an empty roster every position is equally needed, so the term had
 * zero variance exactly when the draft is young and most picks get made.
 *
 * This is the per-player version. The quantity is deliberately expressed as a
 * *difference* from generic VOR rather than as a raw lineup gain:
 *
 *     fit = (points he adds to your optimal lineup) − (points over replacement)
 *
 * On an open roster those are the same thing and the fit is 0, which is correct
 * — your roster has nothing to say yet, and the value term already said it. It
 * only becomes non-zero once your roster makes a player worth *less* to you than
 * he would be to an empty one, which is the only information a need term can
 * add that VOR does not already carry. Expressing it as a raw lineup gain
 * instead would restate VOR under a second name and double-weight it.
 *
 * ## What this cannot see
 *
 * Players already on your roster have no projection attached — the projection
 * pass runs over the *available* pool, and a drafted player's positional rank at
 * the time he was taken is not recoverable. So when a position's starting and
 * flex slots are all full, this values a further body as insurance only, and
 * never as an upgrade over a starter you already own. That is the conservative
 * direction: it under-credits the rare case where an elite player falls to a
 * roster that is already full at his position, rather than inventing a number
 * for the incumbent he would displace.
 */
export function rosterFitPoints(
  roster: PlayerId[],
  league: LeagueConfig,
  positionOf: (id: PlayerId) => Position | null,
  baselines: BaselineResult,
): (position: Position, projPoints: number) => number {
  const owned = {} as Record<Position, number>;
  for (const pos of FANTASY_POSITIONS) owned[pos] = 0;
  for (const id of roster) {
    const pos = positionOf(id);
    if (pos) owned[pos]++;
  }

  // Walk the same starter-then-flex fill as `computeRosterNeed` so the two
  // agree about what "open" means.
  const openStarters = {} as Record<Position, number>;
  const spare = { ...owned };
  for (const pos of FANTASY_POSITIONS) {
    const required = league.starters_by_position[pos] ?? 0;
    const filled = Math.min(spare[pos], required);
    spare[pos] -= filled;
    openStarters[pos] = required - filled;
  }

  const openFlex = league.flex_slots.map((f) => ({ ...f }));
  for (const flex of openFlex) {
    while (flex.count > 0) {
      const donor = flex.eligible.find((p) => spare[p] > 0);
      if (!donor) break;
      spare[donor]--;
      flex.count--;
    }
  }

  return (position: Position, projPoints: number): number => {
    const replacement = baselines.points[position] ?? 0;
    const vor = projPoints - replacement;

    if (openStarters[position] > 0) {
      // He starts. The player he is replacing is the replacement-level one you
      // would otherwise have to use, which is exactly what VOR already priced.
      return 0;
    }

    const flex = openFlex.find((f) => f.count > 0 && f.eligible.includes(position));
    if (flex) {
      // He fills a flex slot, so his real alternative is the best replacement
      // among *every* position that could fill it — usually a stiffer bar than
      // his own position's replacement, and never a softer one.
      const flexBar = Math.max(...flex.eligible.map((p) => baselines.points[p] ?? 0));
      return projPoints - flexBar - vor;
    }

    // Starters and flex are full at this position: he is depth. Depth is
    // discounted, not zeroed — starters get hurt, and the best bench player at
    // a deep position routinely outscores the worst starter at a shallow one.
    // Zeroing it is the classic way a draft tool talks you into a kicker in
    // round nine.
    //
    // `spare` counts bodies already sitting behind the starters *and* the flex,
    // which is what "how much depth do you already have here" means. Counting
    // from the starter requirement instead would charge a roster for the player
    // it has standing in its own flex slot, and dock the first genuine bench
    // back as if he were the second.
    const insurance = 0.5 / (1 + spare[position] * 0.7);
    return insurance * vor - vor;
  };
}

export interface RosterSlotAssignment {
  /** Starter slots actually filled, by base position. */
  starters: { position: Position; playerIds: PlayerId[] }[];
  /** Flex slots actually filled, by flex kind. */
  flex: { kind: string; eligible: Position[]; playerIds: PlayerId[] }[];
  /** Everyone left over once starters and flex slots are filled (FR-38). */
  bench: PlayerId[];
}

/**
 * Slot a drafted roster into starters, flex, and bench (FR-38).
 *
 * `computeRosterNeed` above answers "how many slots are still open" for the
 * recommendation engine; this answers "who is standing in which slot right
 * now" for the roster panel — a flex or bench player is a real, distinct
 * outcome of a draft pick and the UI must be able to say so, not silently
 * fold every drafted player into their raw position count.
 */
export function assignRosterSlots(
  roster: PlayerId[],
  league: LeagueConfig,
  positionOf: (id: PlayerId) => Position | null,
): RosterSlotAssignment {
  const byPosition = new Map<Position, PlayerId[]>();
  for (const id of roster) {
    const pos = positionOf(id);
    if (!pos) continue;
    const list = byPosition.get(pos) ?? [];
    list.push(id);
    byPosition.set(pos, list);
  }

  const starters: { position: Position; playerIds: PlayerId[] }[] = [];
  const leftover = new Map<Position, PlayerId[]>();

  for (const pos of FANTASY_POSITIONS) {
    const required = league.starters_by_position[pos] ?? 0;
    const owned = byPosition.get(pos) ?? [];
    starters.push({ position: pos, playerIds: owned.slice(0, required) });
    leftover.set(pos, owned.slice(required));
  }

  const flex = league.flex_slots.map((f) => ({ kind: f.kind, eligible: f.eligible, playerIds: [] as PlayerId[] }));

  // Fill each flex slot up to its count, cheapest-first is not meaningful here,
  // so pull from whichever eligible position still has leftovers.
  for (const [slot, def] of flex.map((s, i) => [s, league.flex_slots[i]!] as const)) {
    while (slot.playerIds.length < def.count) {
      const donorPos = slot.eligible.find((p) => (leftover.get(p) ?? []).length > 0);
      if (!donorPos) break;
      const donorList = leftover.get(donorPos)!;
      slot.playerIds.push(donorList.shift()!);
    }
  }

  const bench: PlayerId[] = [];
  for (const pos of FANTASY_POSITIONS) bench.push(...(leftover.get(pos) ?? []));

  return { starters, flex, bench };
}

// ---------------------------------------------------------------------------
// Bye-week conflicts (FR-26)
// ---------------------------------------------------------------------------

/**
 * Penalty for stacking a bye week at a position where the user would then be
 * unable to field a starter.
 *
 * Returns 0 unless taking this player would leave the roster short at his
 * position on his bye. Deliberately small in the default weights: bye conflicts
 * are a real cost but a tiebreaker-sized one, and a tool that lets bye weeks
 * override talent in round three is a tool that loses drafts.
 */
export function byeConflictPenalty(
  candidate: Player,
  roster: PlayerId[],
  league: LeagueConfig,
  playerOf: (id: PlayerId) => Player | undefined,
): number {
  const bye = candidate.bye_week;
  const pos = candidate.position;
  if (!bye || !pos) return 0;

  const required = league.starters_by_position[pos] ?? 0;
  if (required === 0) return 0;

  let sameByeAtPosition = 0;
  let totalAtPosition = 0;
  for (const id of roster) {
    const p = playerOf(id);
    if (!p || p.position !== pos) continue;
    totalAtPosition++;
    if (p.bye_week === bye) sameByeAtPosition++;
  }

  const totalAfter = totalAtPosition + 1;

  // Nothing is being *stacked* unless somebody already on the roster at this
  // position shares the bye. Without this test the penalty fires on your first
  // quarterback — you own none, so taking one does leave you with no starter in
  // his bye week — which is true of literally every quarterback and therefore
  // tells you nothing, while systematically marking down every single-slot
  // position against RB and WR. The guard below hid that for the multi-slot
  // positions and not for QB, TE, K or DEF, which is what made it look like
  // intended behaviour rather than an artifact.
  if (sameByeAtPosition === 0) return 0;

  // Below the starting requirement there is no conflict to speak of: the user
  // has not finished filling the position and will draft more. Penalising a
  // first running back for "leaving you short on his bye" would be nonsense —
  // every first pick at a position leaves you short until the second arrives.
  if (totalAfter < required) return 0;

  // Adding this player makes it sameByeAtPosition + 1 on that week.
  const onByeAfter = sameByeAtPosition + 1;
  const availableThatWeek = totalAfter - onByeAfter;
  if (availableThatWeek >= required) return 0;

  const shortfall = required - availableThatWeek;
  return clamp01(shortfall / Math.max(required, 1));
}

// ---------------------------------------------------------------------------
// Tiering
// ---------------------------------------------------------------------------

/**
 * Derive tiers from projected points when the ranking source does not supply
 * them (FR-10 needs tiers to detect breaks, and most sources omit them).
 *
 * A tier boundary is a **cliff relative to its surroundings**, not simply a
 * large gap. That distinction is the whole algorithm: a positional curve decays
 * steeply at the top and flattens out, so the absolute gaps between the top few
 * players are the largest on the board. Thresholding on the mean gap therefore
 * puts a tier break between almost every elite player and declares each of them
 * a one-man tier — which then makes the UI announce "last player in tier 1" at
 * every position simultaneously, which is noise.
 *
 * Comparing each gap to the *local* median instead adapts to how steep the
 * curve is at that point, so a break is flagged only where the curve does
 * something its neighbourhood does not.
 */
export function deriveTiers(pool: PoolEntry[], sensitivity = 2.2): Map<PlayerId, number> {
  const out = new Map<PlayerId, number>();
  const byPosition = groupByPosition(pool);
  const WINDOW = 9;

  for (const [, sorted] of byPosition) {
    if (sorted.length === 0) continue;

    const gaps: number[] = [];
    for (let i = 1; i < sorted.length; i++) {
      gaps.push(Math.max(0, sorted[i - 1]!.proj_points - sorted[i]!.proj_points));
    }

    let tier = 1;
    out.set(sorted[0]!.player.player_id_sleeper, tier);

    for (let i = 1; i < sorted.length; i++) {
      const gap = gaps[i - 1]!;
      const local = median(
        gaps.slice(Math.max(0, i - 1 - WINDOW), Math.min(gaps.length, i - 1 + WINDOW + 1)),
      );
      if (local > 0 && gap > local * sensitivity) tier++;
      out.set(sorted[i]!.player.player_id_sleeper, tier);
    }
  }
  return out;
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

/** Build the pool the engine operates on from ranks and projections. */
export function buildPool(
  players: Iterable<Player>,
  positionRank: (id: PlayerId) => number | null,
  overallRank: (id: PlayerId) => number | null,
  projections: Map<PlayerId, Projection>,
): PoolEntry[] {
  const out: PoolEntry[] = [];
  for (const player of players) {
    const pos = player.position;
    if (!pos) continue;
    const pr = positionRank(player.player_id_sleeper);
    if (pr === null) continue;
    const proj = projections.get(player.player_id_sleeper);
    out.push({
      player,
      position: pos,
      proj_points: proj?.projected_points_league_scoring ?? 0,
      position_rank: pr,
      overall_rank: overallRank(player.player_id_sleeper),
    });
  }
  return out;
}
